import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, AlertCircle } from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import { WideOpener } from "../components/ImageBlocks";
import { supabase } from "../lib/supabase";
import { containsBadWords } from "../lib/badWords";
import { useLanguage } from "../contexts/LanguageContext";

const MAX_LEN = 1000;
const MIN_LEN = 2;
const RATE_LIMIT_MS = 30_000; // 30s zwischen Kommentaren pro Browser

function formatRelative(date, lang = "de") {
  try {
    const diff = (Date.now() - new Date(date).getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    if (diff < 60) return rtf.format(-Math.round(diff), "second");
    if (diff < 3600) return rtf.format(-Math.round(diff / 60), "minute");
    if (diff < 86400) return rtf.format(-Math.round(diff / 3600), "hour");
    if (diff < 30 * 86400) return rtf.format(-Math.round(diff / 86400), "day");
    return new Date(date).toLocaleDateString(lang);
  } catch {
    return new Date(date).toLocaleDateString();
  }
}

export default function Stimmen() {
  const { lang } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const lastPostRef = useRef(0);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from("comments")
        .select("id, body, display_name, created_at, language")
        .order("created_at", { ascending: false })
        .limit(200);
      if (!cancelled) {
        if (err) {
          console.error(err);
          setError("Kommentare konnten nicht geladen werden.");
        } else {
          setComments(data || []);
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel("comments-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "comments" },
        (payload) => {
          setComments((prev) => {
            if (prev.some((c) => c.id === payload.new.id)) return prev;
            return [payload.new, ...prev];
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setInfo("");

    const trimmedBody = body.trim();
    const trimmedName = name.trim().slice(0, 40);

    if (trimmedBody.length < MIN_LEN) {
      setError("Bitte schreibe etwas mehr (mindestens 2 Zeichen).");
      return;
    }
    if (trimmedBody.length > MAX_LEN) {
      setError(`Maximal ${MAX_LEN} Zeichen.`);
      return;
    }
    if (containsBadWords(trimmedBody) || containsBadWords(trimmedName)) {
      setError(
        "Dein Kommentar enthält Worte, die wir nicht zulassen. Bitte formuliere es anders."
      );
      return;
    }

    const now = Date.now();
    if (now - lastPostRef.current < RATE_LIMIT_MS) {
      const wait = Math.ceil(
        (RATE_LIMIT_MS - (now - lastPostRef.current)) / 1000
      );
      setError(`Bitte warte noch ${wait} Sekunden vor dem nächsten Kommentar.`);
      return;
    }

    setSubmitting(true);
    const { error: err } = await supabase.from("comments").insert({
      body: trimmedBody,
      display_name: trimmedName || null,
      language: lang,
    });
    setSubmitting(false);

    if (err) {
      console.error(err);
      setError("Senden fehlgeschlagen. Bitte später erneut versuchen.");
      return;
    }
    lastPostRef.current = now;
    setBody("");
    setName("");
    setInfo("Danke! Dein Kommentar ist jetzt sichtbar.");
    setTimeout(() => setInfo(""), 4000);
  }

  return (
    <PageLayout>
      <WideOpener
        src="wide-3-4_resonanz_21-9_v01.jpg"
        alt="Stimmen"
        heading="Stimmen"
      />

      <div
        className="mx-auto w-full max-w-3xl"
        style={{ color: "#1A1A1A" }}
        data-testid="page-stimmen"
      >
        <p className="text-[17px] leading-[1.6] sm:text-[18px]">
          Schreibe einen anonymen Gedanken, eine Erinnerung oder einen Gruß.
          Kein Login, kein Name nötig – nur deine Stimme.
        </p>

        {/* Eingabe-Formular */}
        <form
          onSubmit={handleSubmit}
          className="mt-8 rounded border p-5"
          style={{
            borderColor: "rgba(42,38,36,0.18)",
            backgroundColor: "rgba(42,38,36,0.03)",
          }}
          data-testid="comment-form"
        >
          <label
            className="text-xs uppercase tracking-[0.2em]"
            style={{ color: "rgba(26,26,26,0.55)", fontWeight: 500 }}
          >
            Dein Name (optional)
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Anonym"
            maxLength={40}
            data-testid="comment-name"
            className="mt-2 block w-full rounded border bg-white px-4 py-3 text-base outline-none focus:border-black/40"
            style={{ borderColor: "rgba(42,38,36,0.2)" }}
          />

          <label
            className="mt-5 block text-xs uppercase tracking-[0.2em]"
            style={{ color: "rgba(26,26,26,0.55)", fontWeight: 500 }}
          >
            Dein Kommentar
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Schreib hier deinen Gedanken …"
            rows={4}
            maxLength={MAX_LEN}
            data-testid="comment-body"
            className="mt-2 block w-full resize-y rounded border bg-white px-4 py-3 text-base outline-none focus:border-black/40"
            style={{ borderColor: "rgba(42,38,36,0.2)" }}
          />
          <div
            className="mt-1 text-right text-xs"
            style={{ color: "rgba(26,26,26,0.5)" }}
          >
            {body.length} / {MAX_LEN}
          </div>

          {error ? (
            <div
              data-testid="comment-error"
              className="mt-3 flex items-start gap-2 text-sm"
              style={{ color: "#a32a2a" }}
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {info ? (
            <div
              data-testid="comment-info"
              className="mt-3 text-sm"
              style={{ color: "#2A6B2A" }}
            >
              {info}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting || body.trim().length < MIN_LEN}
            data-testid="comment-submit"
            className="mt-5 inline-flex items-center gap-2 text-base text-white transition-colors duration-200 hover:opacity-90 disabled:opacity-40"
            style={{
              backgroundColor: "#2A2624",
              padding: "12px 22px",
              borderRadius: "5px",
              fontWeight: 500,
              letterSpacing: "0.3px",
            }}
          >
            <Send className="h-4 w-4" />
            <span>{submitting ? "Sende …" : "Kommentar senden"}</span>
          </button>
        </form>

        {/* Liste */}
        <div className="mt-12">
          <div className="mb-5 flex items-center gap-2">
            <MessageCircle className="h-5 w-5" style={{ color: "#2A2624" }} />
            <h2
              className="text-[22px] sm:text-[24px]"
              style={{ color: "#1A1A1A", fontWeight: 600 }}
            >
              Stimmen aus der Community ({comments.length})
            </h2>
          </div>

          {loading ? (
            <p style={{ color: "rgba(26,26,26,0.55)" }}>Lade Kommentare …</p>
          ) : comments.length === 0 ? (
            <p style={{ color: "rgba(26,26,26,0.55)" }}>
              Noch keine Kommentare. Sei die erste Stimme.
            </p>
          ) : (
            <ul className="space-y-5" data-testid="comments-list">
              {comments.map((c) => (
                <li
                  key={c.id}
                  data-testid={`comment-${c.id}`}
                  className="rounded border p-5"
                  style={{
                    borderColor: "rgba(42,38,36,0.12)",
                    backgroundColor: "rgba(42,38,36,0.02)",
                  }}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span
                      className="text-sm"
                      style={{ color: "#1A1A1A", fontWeight: 600 }}
                    >
                      {c.display_name || "Anonym"}
                    </span>
                    <span
                      className="text-xs"
                      style={{ color: "rgba(26,26,26,0.5)" }}
                    >
                      {formatRelative(c.created_at, lang)}
                    </span>
                  </div>
                  <p
                    className="mt-2 whitespace-pre-wrap text-[16px] leading-[1.55]"
                    style={{ color: "#1A1A1A" }}
                  >
                    {c.body}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
